// frontend/src/components/mobile/mobileNavigationConfig.js
// Project:      Vision Systems / Vision ANPR
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-23
// Updated:      2026-09-23 (wire Quote creation and Sterling action icon)
//
// Description:  Canonical presentation metadata for Vision mobile navigation.
//               Permission identifiers are deliberately supplied by the caller;
//               this file does not invent or bypass Vision's RBAC vocabulary.
//               PWA-003 exposes only real current destinations in the persistent
//               bar: My Day, Home, Jobs and the existing profile surface.

import {
  faBriefcase,
  faBuilding,
  faCalendarDay,
  faChartLine,
  faHome,
  faReceipt,
  faSterlingSign,
  faTasks,
  faUser,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';

export const VISION_MOBILE_NAVIGATION_ITEMS = Object.freeze([
  Object.freeze({ id: 'my-day', label: 'My Day', icon: faCalendarDay }),
  Object.freeze({ id: 'home', label: 'Home', icon: faHome }),
  Object.freeze({ id: 'jobs', label: 'Jobs', icon: faBriefcase }),
  Object.freeze({ id: 'profile', label: 'Me', icon: faUser }),
]);

const QUICK_ACTION_DEFINITIONS = Object.freeze([
  Object.freeze({
    id: 'job.create',
    label: 'New Job',
    icon: faBriefcase,
    handlerKey: 'openJobCreate',
  }),
  Object.freeze({
    id: 'quote.create',
    label: 'New Quote',
    icon: faSterlingSign,
    handlerKey: 'openQuoteCreate',
  }),
  Object.freeze({
    id: 'customer.create',
    label: 'New Customer',
    icon: faBuilding,
    handlerKey: 'openCustomerCreate',
  }),
  Object.freeze({
    id: 'opportunity.create',
    label: 'New Opportunity',
    icon: faChartLine,
    handlerKey: 'openOpportunityCreate',
  }),
  Object.freeze({
    id: 'contact.create',
    label: 'New Contact',
    icon: faUserPlus,
    handlerKey: 'openContactCreate',
  }),
  Object.freeze({
    id: 'expense.create',
    label: 'Add Expense',
    icon: faReceipt,
    handlerKey: 'openExpenseCreate',
  }),
  Object.freeze({
    id: 'task.create',
    label: 'New Task',
    icon: faTasks,
    handlerKey: 'openTaskCreate',
  }),
]);

/**
 * Builds the complete design-stage action list. Actions with an existing shell
 * handler and an allowed permission rule are enabled. All other planned actions
 * remain visible but disabled so the radial design can be assessed before their
 * creation workflows are wired. Backend authorization remains authoritative.
 */
export function buildVisionMobileQuickActions({
  canAccess = () => false,
  handlers = {},
  permissionByAction = {},
} = {}) {
  return QUICK_ACTION_DEFINITIONS.map((definition) => {
    const handler = handlers[definition.handlerKey];
    const hasPermissionRule = Object.prototype.hasOwnProperty.call(
      permissionByAction,
      definition.id,
    );
    const permission = permissionByAction[definition.id];
    const hasHandler = typeof handler === 'function';
    const hasPermission = hasPermissionRule
      && (!permission || canAccess(permission));
    const isEnabled = hasHandler && hasPermission;

    return {
      id: definition.id,
      label: definition.label,
      icon: definition.icon,
      disabled: !isEnabled,
      onSelect: isEnabled ? handler : null,
    };
  });
}

export function runVisionMobileQuickAction(actionId, action) {
  if (!action || action.id !== actionId || typeof action.onSelect !== 'function') {
    throw new Error(`No Vision quick-action handler is registered for "${actionId}".`);
  }

  action.onSelect();
}
